import { MoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, Empty, MenuProps, Radio, Splitter } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import {
  ZAttrPermission,
  ZAttrRoleAccess,
} from "src/business/access/ZAttrAccess";
import { CollapseButton } from "src/components/CollapseButton";

import { EditableAttrAccess, getVisibleAttrs } from "../EditableSpecAccess";
import {
  changeColumn,
  SpecificationsAccessStore,
} from "../SpecificationsAccessStore";
import * as styles from "./SingleSpecAccessEditor.module.less";

type PropsSingleSpecAccessEditor = {
  attributes: EditableAttrAccess[];
  store: SpecificationsAccessStore;
};

export const SingleSpecAccessEditor: React.FC<PropsSingleSpecAccessEditor> =
  observer((props) => {
    const { attributes, store } = props;
    const { visibleColumns } = store;
    const visibleAttrs = getVisibleAttrs(attributes);
    const rolesBoxStyle: React.CSSProperties = {
      gridTemplateColumns: `repeat(${visibleColumns.length}, 200px)`,
    };
    const changeRow = (mode: ZAttrPermission, attr: EditableAttrAccess) => {
      attr.roles.forEach((role) => {
        // eslint-disable-next-line no-param-reassign
        role.permission = mode;
      });
    };
    if (attributes.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Нет атрибутов"
        />
      );
    }
    return (
      <Splitter>
        <Splitter.Panel defaultSize="25%" min="15%" max="50%">
          <div className={styles.panelFrame}>
            <div className={styles.panelContent}>
              <div className={styles.headerCell}>Атрибут</div>
              {visibleAttrs.map((attr) => (
                <div
                  key={attr.id}
                  className={styles.attrNameCell}
                  style={{ marginLeft: attr.level * 24 }}
                >
                  <div>
                    {typeof attr.collapsed === "boolean" && (
                      <CollapseButton
                        collapsed={attr.collapsed}
                        onChange={(v) => {
                          // и тут будет ворнинг мобикса
                          // eslint-disable-next-line no-param-reassign
                          attr.collapsed = v;
                        }}
                      />
                    )}
                  </div>
                  <div>{attr.label}</div>
                  <div>
                    <OpMenu
                      title="Доступ к атрибуту для всех ролей:"
                      action={(mode) => changeRow(mode, attr)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Splitter.Panel>
        <Splitter.Panel>
          <div className={styles.panelFrame}>
            <div className={styles.panelContent} style={rolesBoxStyle}>
              {/* Заголовки ролей */}
              {visibleColumns.map(({ label, value }) => (
                <div key={value} className={styles.headerCell}>
                  <div className={styles.headerTitle}>{label}</div>
                  <OpMenu
                    title="Доступ роли к всем атрибутам:"
                    action={(mode) => changeColumn(attributes, mode, value)}
                  />
                </div>
              ))}
              {visibleAttrs.map(({ id, roles }) => (
                <React.Fragment key={id}>
                  {visibleColumns.map(({ value: roleId }) => (
                    <div key={roleId} className={styles.cell}>
                      <AttrCell roleId={roleId} roles={roles} />
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </Splitter.Panel>
      </Splitter>
    );
  });

type PropsAttrCell = {
  roleId: string;
  roles: ZAttrRoleAccess[];
};

const AttrCell: React.FC<PropsAttrCell> = observer(({ roleId, roles }) => {
  const roleItem = roles.find(({ id }) => roleId === id);
  if (!roleItem) return null;
  return (
    <Radio.Group
      block
      options={shortOptions}
      optionType="button"
      value={roleItem.permission}
      onChange={(e) => {
        // Тут будет вылазить ворнинг мобикса.
        roleItem.permission = e.target.value;
      }}
    />
  );
});

const shortOptions: { value: ZAttrPermission; label: string }[] = [
  { value: "noAccess", label: "-" },
  { value: "readOnly", label: "R" },
  { value: "readWrite", label: "RU" },
];

const menuOptions: [ZAttrPermission, string][] = [
  ["noAccess", "Скрыть (-)"],
  ["readOnly", "Чтение (Read)"],
  ["readWrite", "Чтение и обновление (Read&Update)"],
];

type PropsOpMenu = {
  title: string;
  action: (value: ZAttrPermission) => void;
};

const OpMenu: React.FC<PropsOpMenu> = ({ title, action }) => {
  const items: MenuProps["items"] = [
    {
      key: "group",
      label: title,
      type: "group",
      children: menuOptions.map(([key, label]) => ({
        key,
        label,
        onClick: () => action(key),
      })),
    },
  ];
  return (
    <Dropdown menu={{ items }} placement="bottomRight">
      <Button type="text" icon={<MoreOutlined />} />
    </Dropdown>
  );
};
